import { useEffect, useRef, useState } from "react"

interface UseCarouselScrollOptions {
  itemCount: number
  indexSwitchDelta?: number
}

export function useCarouselScroll({ itemCount, indexSwitchDelta = 30 }: UseCarouselScrollOptions) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const carouselRef = useRef<HTMLDivElement>(null)
  const scrollEndTimerRef = useRef<number | null>(null)
  const isNavigatingRef = useRef(false)

  const handleScroll = () => {
    if (!carouselRef.current) return

    const container = carouselRef.current
    const cards = container.querySelectorAll<HTMLElement>('[data-adv-card="true"]')
    const containerRect = container.getBoundingClientRect()
    const containerCenter = containerRect.left + containerRect.width / 2

    let closestIndex = 0
    let closestDistance = Number.POSITIVE_INFINITY
    let currentDistance = Number.POSITIVE_INFINITY

    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect()
      const cardCenter = cardRect.left + cardRect.width / 2
      const distance = Math.abs(cardCenter - containerCenter)
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
      if (index === currentSlide) {
        currentDistance = distance
      }
    })

    const shouldSwitch = closestIndex !== currentSlide && currentDistance - closestDistance > indexSwitchDelta

    if (!isNavigatingRef.current && shouldSwitch) setCurrentSlide(closestIndex)

    if (scrollEndTimerRef.current) window.clearTimeout(scrollEndTimerRef.current)
    scrollEndTimerRef.current = window.setTimeout(() => {
      isNavigatingRef.current = false
      if (shouldSwitch) setCurrentSlide(closestIndex)
    }, 120)
  }

  const scrollToSlide = (index: number) => {
    if (!carouselRef.current) return
    if (isNavigatingRef.current) return

    isNavigatingRef.current = true
    setCurrentSlide(index)

    const container = carouselRef.current
    const cards = container.querySelectorAll<HTMLElement>('[data-adv-card="true"]')
    const target = cards[index]

    if (target) {
      target.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" })
    }
  }

  const prevSlide = () => scrollToSlide(Math.max(currentSlide - 1, 0))
  const nextSlide = () => scrollToSlide(Math.min(currentSlide + 1, itemCount - 1))

  useEffect(() => {
    const carousel = carouselRef.current
    carousel?.addEventListener("scroll", handleScroll)
    return () => carousel?.removeEventListener("scroll", handleScroll)
  }, [currentSlide])

  return {
    currentSlide,
    carouselRef,
    scrollToSlide,
    prevSlide,
    nextSlide,
    setCurrentSlide,
  }
}
